// src/components/AgendarDialog/EditPopoverContent.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Button, TimePicker, message } from "antd";
import PropTypes from "prop-types";
import dayjs from "../../../../utils/dayjs";

const EditPopoverContent = ({
  idaTime,
  setIdaTime,
  llegadaTime,
  setLlegadaTime,
  retornoRecogidaTime,
  setRetornoRecogidaTime,
  retornoLlegadaTime,
  setRetornoLlegadaTime,
  handleSaveTimes,
  form,
  selectedDay,
}) => {
  const [validationError, setValidationError] = useState(null);

  // Validar que las horas estén en orden correcto
  const validateTimes = () => {
    // Validar hora de llegada después de hora de recogida (al menos 15 minutos)
    if (idaTime && llegadaTime) {
      const diffMinutes = llegadaTime.diff(idaTime, "minute");
      if (diffMinutes < 15) {
        return "La hora de llegada debe ser al menos 15 minutos después de la hora de recogida";
      }
    }

    // Validar hora de retorno recogida después de hora de llegada (al menos 15 minutos)
    if (llegadaTime && retornoRecogidaTime) {
      const diffMinutes = retornoRecogidaTime.diff(llegadaTime, "minute");
      if (diffMinutes < 15) {
        return "La hora de retorno (recogida) debe ser al menos 15 minutos después de la hora de llegada";
      }
    }

    // Validar hora de retorno llegada después de hora de retorno recogida (al menos 15 minutos)
    if (retornoRecogidaTime && retornoLlegadaTime) {
      const diffMinutes = retornoLlegadaTime.diff(retornoRecogidaTime, "minute");
      if (diffMinutes < 15) {
        return "La hora de retorno (llegada) debe ser al menos 15 minutos después de la hora de retorno (recogida)";
      }
    }

    return null;
  };

  // Revalidar cuando cambien las horas
  useEffect(() => {
    setValidationError(validateTimes());
  }, [idaTime, llegadaTime, retornoRecogidaTime, retornoLlegadaTime]);

  // Guardar con validación
  const handleSaveWithValidation = () => {
    const error = validateTimes();
    if (error) {
      message.error(error);
      return;
    }
    handleSaveTimes();
  };
  // Verificar si el día de ida es hoy
  const isIdaToday = useMemo(() => {
    if (!selectedDay?.ida) return false;
    return dayjs(selectedDay.ida).isSame(dayjs(), "day");
  }, [selectedDay?.ida]);

  // Verificar si el día de retorno es hoy
  const isRetornoToday = useMemo(() => {
    if (!selectedDay?.retorno) return false;
    return dayjs(selectedDay.retorno).isSame(dayjs(), "day");
  }, [selectedDay?.retorno]);

  // Función para deshabilitar horas pasadas cuando el día es hoy
  const getDisabledTime = (isToday) => {
    if (!isToday) return {};

    const now = dayjs();
    const currentHour = now.hour();
    const currentMinute = now.minute();

    return {
      disabledHours: () => {
        const hours = [];
        for (let i = 0; i < currentHour; i++) {
          hours.push(i);
        }
        return hours;
      },
      disabledMinutes: (selectedHour) => {
        if (selectedHour === currentHour) {
          const minutes = [];
          for (let i = 0; i <= currentMinute; i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      },
    };
  };

  return (
  <div
    style={{ display: "flex", flexDirection: "column" }}
    onClick={(e) => e.stopPropagation()}
    role="presentation" // Indica que el div no es interactivo
    tabIndex={-1} // Evita que el div sea enfocado por el teclado
  >
    {selectedDay?.ida && (
      <>
        <label htmlFor="hora-recogida">Hora de Recogida:</label>
        <TimePicker
          id="hora-recogida"
          value={idaTime}
          onChange={setIdaTime}
          placeholder="Hora de Recogida"
          format="HH:mm"
          style={{ marginBottom: 8 }}
          disabledTime={() => getDisabledTime(isIdaToday)}
        />
        <label htmlFor="hora-llegada">Hora de Llegada:</label>
        <TimePicker
          id="hora-llegada"
          value={llegadaTime}
          onChange={setLlegadaTime}
          placeholder="Hora de Llegada"
          format="HH:mm"
          style={{ marginBottom: 8 }}
          disabledTime={() => getDisabledTime(isIdaToday)}
        />
      </>
    )}
    {selectedDay?.retorno && (
      <>
        <label htmlFor="hora-retorno-recogida">
          Hora de Retorno (Recogida):
        </label>
        <TimePicker
          id="hora-retorno-recogida"
          value={retornoRecogidaTime}
          onChange={setRetornoRecogidaTime}
          placeholder="Hora de Recogida Retorno"
          format="HH:mm"
          style={{ marginBottom: 8 }}
          disabled={!form?.getFieldValue("hora_retorno_recogida")}
          disabledTime={() => getDisabledTime(isRetornoToday)}
        />
        <label htmlFor="hora-retorno-llegada">Hora de Retorno (Llegada):</label>
        <TimePicker
          id="hora-retorno-llegada"
          value={retornoLlegadaTime}
          onChange={setRetornoLlegadaTime}
          placeholder="Hora de Llegada Retorno"
          format="HH:mm"
          style={{ marginBottom: 8 }}
          disabled={!form?.getFieldValue("hora_retorno_llegada")}
          disabledTime={() => getDisabledTime(isRetornoToday)}
        />
      </>
    )}
    {validationError && (
      <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: 4, marginBottom: 4 }}>
        {validationError}
      </div>
    )}
    <Button
      type="primary"
      onClick={handleSaveWithValidation}
      style={{ marginTop: 8 }}
      disabled={!!validationError}
    >
      Aceptar
    </Button>
  </div>
  );
};

EditPopoverContent.propTypes = {
  idaTime: PropTypes.object,
  setIdaTime: PropTypes.func.isRequired,
  llegadaTime: PropTypes.object,
  setLlegadaTime: PropTypes.func.isRequired,
  retornoRecogidaTime: PropTypes.object,
  setRetornoRecogidaTime: PropTypes.func.isRequired,
  retornoLlegadaTime: PropTypes.object,
  setRetornoLlegadaTime: PropTypes.func.isRequired,
  handleSaveTimes: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  selectedDay: PropTypes.object,
};

export default EditPopoverContent;
